"use client";

import { useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { motion } from "framer-motion";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import {
  usePublicReferralInvite,
  usePublicRevenueImpact,
  trackReferralClick,
} from "../services/referralClient";
import { getStoredReferralAttribution } from "../services/referralStorage";
import { useReferralAnalytics } from "../hooks/useReferralAnalytics";
import { InviteLandingNavbar } from "./InviteLandingNavbar";
import { buildAppLoginUrl, getPortalBaseUrl } from "@/lib/utils";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
  Skeleton
} from "@artha/ui";
import {
  StarIcon,
  CloverShape,
  FlowerShape,
  BauhausShape,
  BrutalismShape,
  PinkStripeIcon,
  BlueStripeIcon,
  WaveShape,
  ImpactLeftShape,
} from "@/components/icons";
import {
  REFERRAL_INVITE_TESTIMONIALS,
  REFERRAL_INVITE_URLS,
} from "./referralDashboard.constants";

const formatImpactCount = (value: number) => {
  if (value >= 1_000_000) return `${Math.floor(value / 100_000) / 10}M+`;
  if (value >= 1_000) return `${Math.floor(value / 100) / 10}K+`;
  return `${Math.floor(value)}+`;
};

type InviteLandingPageClientProps = {
  creatorUniqueId: string;
};

export function InviteLandingPageClient({ creatorUniqueId }: InviteLandingPageClientProps) {
  const t = useTranslations("referralInvite");
  const trackedRef = useRef(false);
  const [playingVideo, setPlayingVideo] = useState<string | null>(null);
  const [isStartingSignup, setIsStartingSignup] = useState(false);
  const { data, isLoading, isError } = usePublicReferralInvite(creatorUniqueId);
  const { data: impact } = usePublicRevenueImpact(data?.id);
  const { trackReferAndEarnInvitation } = useReferralAnalytics();
  const pinkTickerTexts = t.raw("tickers.pink") as string[];
  const blueTickerTexts = t.raw("tickers.blue") as string[];

  useEffect(() => {
    if (!isError) return;
    window.location.href = getPortalBaseUrl();
  }, [isError]);

  useEffect(() => {
    if (trackedRef.current) return;
    trackedRef.current = true;
    trackReferralClick(creatorUniqueId).catch(() => {
      // Signup must keep working even when attribution tracking fails.
    });
  }, [creatorUniqueId]);

  const handleSignupClick = async () => {
    if (isStartingSignup) return;

    trackReferAndEarnInvitation(creatorUniqueId);
    setIsStartingSignup(true);
    try {
      let stored = getStoredReferralAttribution();
      if (!stored || stored.creatorUniqueId !== creatorUniqueId) {
        const click = await trackReferralClick(creatorUniqueId);
        stored = {
          attributionToken: click.attributionToken,
          creatorUniqueId: click.creatorUniqueId,
          expiresAt: click.expiresAt,
        };
      }

      const loginUrl = buildAppLoginUrl({
        referralToken: stored?.attributionToken,
      });
      window.location.href = loginUrl;
    } catch {
      window.location.href = buildAppLoginUrl();
    }
  };

  if (isLoading || !data) {
    return (
      <main className="min-h-screen bg-referral-cream px-6 py-32 lg:py-32">
        <div className="mx-auto max-w-7xl">
          <div className="grid grid-cols-1 items-center gap-5 lg:grid-cols-2">
            <div className="flex flex-col gap-10">
              <div className="space-y-8">
                {/* Heading Skeleton */}
                <div className="space-y-4">
                  <Skeleton className="h-[44px] w-[80%] rounded-xl border border-black/5" />
                  <Skeleton className="h-[44px] w-[60%] rounded-xl border border-black/5" />
                </div>
                {/* Description Skeleton */}
                <div className="space-y-3">
                  <Skeleton className="h-6 w-full rounded-md border border-black/5" />
                  <Skeleton className="h-6 w-[90%] rounded-md border border-black/5" />
                </div>
              </div>
              {/* Button Skeleton */}
              <Skeleton className="h-16 w-[280px] rounded-xl border border-black/5" />
            </div>

            {/* Collage Skeleton */}
            <div className="relative mx-auto h-[420px] w-full max-w-[500px] sm:h-[500px] sm:max-w-[600px] flex items-center justify-center">
              <Skeleton className="h-[340px] w-[260px] rounded-t-full border border-black/5" />
              <div className="absolute right-4 bottom-10">
                <Skeleton className="h-32 w-32 rounded-full border border-black/5" />
              </div>
              <div className="absolute left-4 top-10">
                <Skeleton className="h-32 w-32 rounded-lg rotate-12 border border-black/5" />
              </div>
            </div>
          </div>

          {/* Impact Stats Skeleton Preview */}
          <div className="mt-32 grid grid-cols-2 gap-8 max-w-4xl mx-auto">
            <Skeleton className="h-40 w-full rounded-2xl border border-black/5" />
            <Skeleton className="h-40 w-full rounded-2xl border border-black/5" />
          </div>
        </div>
      </main>
    );
  }

  return (
    <div className="min-h-screen overflow-x-hidden bg-referral-cream font-sans text-referral-ink selection:bg-referral-pink selection:text-white">
      <style jsx global>{`
        @import url('https://fonts.googleapis.com/css2?family=Bree+Serif&display=swap');
        .font-bree { font-family: 'Bree Serif', serif; }
      `}</style>

      <InviteLandingNavbar onSignupClick={handleSignupClick} />

      <main>
        {/* SECTION 1: HERO */}
        <section className="px-6 py-32 lg:py-36">
          <div className="mx-auto max-w-7xl">
            <div className="grid grid-cols-1 items-center gap-16 lg:grid-cols-2">
              <div className="flex flex-col gap-10">
                <div className="space-y-3">
                  <h1 className="font-bree text-[32px] leading-[1.3] tracking-wide text-referral-ink sm:text-[52px]">
                    <span className="relative inline-block whitespace-nowrap mr-2 rotate-[-3.58deg]">
                      <span className="relative z-10 inline-block px-3 py-0.5 text-white sm:px-4 sm:py-1 sm:text-[46px]">{data.displayName}</span>
                      <span className="absolute inset-0 border border-referral-ink bg-referral-pink shadow-referral-sm" />
                    </span>
                    <span className="text-referral-ink">{t("hero.headingSuffix")}</span>
                  </h1>
                  <p className="max-w-xl text-lg font-plus-jakarta-sans leading-relaxed text-referral-muted sm:text-lg">
                    {t("hero.body")}
                  </p>
                </div>

                <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                  <button
                    type="button"
                    onClick={() => {
                      void handleSignupClick();
                    }}
                    disabled={isStartingSignup}
                    className="inline-flex h-14 items-center justify-center gap-3 whitespace-nowrap rounded-xl border-2 border-referral-ink bg-referral-blue-bright px-8 text-lg font-plus-jakarta-sans text-white shadow-referral-sm transition-all hover:-translate-y-[2px] hover:shadow-referral-lg active:translate-y-[2px] active:shadow-none sm:h-16 sm:px-10 sm:text-xl sm:shadow-referral-md"
                  >
                    {t("hero.cta")}
                    <ArrowRight className="h-6 w-6 stroke-3" />
                  </button>
                </div>
              </div>

              {/* Hero Collage */}
              <div className="relative mx-auto h-[420px] w-full max-w-[500px] sm:h-[500px] sm:max-w-[600px] mt-5 lg:mt-0">
                {/* Decorative Shapes */}
                <motion.div
                  className="absolute right-[2%] bottom-[5%] z-0 rotate-12 opacity-90 sm:right-[-1%] sm:bottom-[50px] w-32 h-32"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 5, repeat: Infinity, ease: "linear" }}
                  style={{ transformOrigin: "50% 50%" }}
                >
                  <FlowerShape className="h-full w-full text-referral-pink-soft" />
                </motion.div>

                {/* Sparkles/Stars */}
                <motion.div
                  className="absolute right-[-2%] top-[15%] z-20 sm:right-[-4%] sm:top-[8%]"
                  animate={{ scale: [1, 1.3, 1] }}
                  transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
                  style={{ transformOrigin: "50% 50%" }}
                >
                  <StarIcon className="h-12 w-12 text-referral-ink sm:h-16 sm:w-16" />
                </motion.div>

                <motion.div
                  className="absolute left-[3%] bottom-0 z-20 sm:left-[15%] sm:top-[60%]"
                  animate={{ scale: [1, 1.3, 1] }}
                  transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
                  style={{ transformOrigin: "50% 50%" }}
                >
                  <StarIcon className="h-14 w-14 text-referral-ink sm:h-20 sm:w-20" />
                </motion.div>

                <motion.div
                  className="absolute left-[-4%] top-[3%] z-0 sm:right-[42%] sm:left-[19%] sm:-top-5 w-32 h-32 sm:w-48 sm:h-48"
                  animate={{ rotate: -360 }}
                  transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
                  style={{ transformOrigin: "50% 50%" }}
                >
                  <CloverShape className="h-full w-full text-referral-teal" />
                </motion.div>

                <div className="absolute left-1/2 top-12 z-10 h-[280px] w-[280px] -translate-x-1/2 overflow-hidden rounded-[34px] border-[3px] border-referral-ink bg-white shadow-referral-lg sm:left-[35%] sm:right-20 sm:top-10 sm:h-[330px] sm:w-[330px] sm:translate-x-0 sm:shadow-referral-xl">
                  {data.avatarUrl ? (
                    <div className="flex h-full w-full items-center justify-center">
                      <img
                        src={data.avatarUrl}
                        alt={data.displayName}
                        className="h-full w-full object-contain object-center"
                      />
                    </div>
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-7xl font-black text-white/50 sm:text-8xl">{data.displayName.slice(0, 1)}</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION: TICKER STRIPES */}
        <div className="relative z-20 -mt-30 mb-20 space-y-[2px]">
          {/* Pink Tilted Stripe */}
          <div className="relative mb-12 -rotate-4 border-y border-referral-ink bg-referral-marquee shadow-referral-sm sm:py-5">
            <motion.div
              className="flex whitespace-nowrap"
              animate={{ x: [0, -1500] }}
              transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
            >
              {[...Array(10)].map((_, i) => (
                <div key={i} className="flex items-center gap-10 px-5">
                  {pinkTickerTexts.map((text, idx) => (
                    <div key={idx} className="flex items-center gap-10">
                      <span className="text-sm sm:text-[42px] leading-[42px] font-bree text-white whitespace-nowrap">
                        {text}
                      </span>
                      <PinkStripeIcon className="h-[50px] w-[50px] shrink-0" />
                    </div>
                  ))}
                </div>
              ))}
            </motion.div>
          </div>

          {/* Blue Stripe */}
          <div className="relative z-10 border-y border-referral-ink bg-referral-blue shadow-referral-sm sm:py-5">
            <motion.div
              className="flex whitespace-nowrap"
              animate={{ x: [-1500, 0] }}
              transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
            >
              {[...Array(10)].map((_, i) => (
                <div key={i} className="flex items-center gap-10 px-5">
                  {blueTickerTexts.map((text, idx) => (
                    <div key={idx} className="flex items-center gap-10">
                      <span className="text-sm sm:text-[42px] leading-[42px] font-bree text-white whitespace-nowrap">
                        {text}
                      </span>
                      <BlueStripeIcon className="h-[50px] w-[50px] shrink-0" />
                    </div>
                  ))}
                </div>
              ))}
            </motion.div>
          </div>
        </div>

        {/* SECTION 2: IMPACT STATS */}
        <section className="relative overflow-hidden bg-referral-marquee py-24">
          {/* Decorative Background Shapes */}
          <div className="absolute top-0 right-0 opacity-100 z-0">
            <WaveShape className="w-[300px] h-auto lg:w-[490px]" />
          </div>
          <div className="absolute left-[50px] top-1/2 -translate-y-1/2 opacity-100 z-0 rotate-15">
            <ImpactLeftShape className="w-[150px] h-[150px] lg:w-[228px] lg:h-[228px]" />
          </div>

          <div className="mx-auto max-w-7xl px-6 text-center relative z-10">
            <h2 className="font-bree text-4xl sm:text-5xl leading-tight text-white flex flex-wrap items-center justify-center gap-x-4">
              {t("impact.headingPrefix")}
              <span className="relative inline-block -rotate-1 mx-2">
                <span className="absolute inset-0 bg-black translate-x-1 translate-y-1 rounded-sm" />
                <span className="relative z-10 block border-2 border-black bg-referral-blue px-6 py-2 text-white">
                  {data.displayName}
                </span>
              </span>
            </h2>

            <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 gap-12 max-w-4xl mx-auto">
              <div className="space-y-4 group">
                <div className="text-[64px] sm:text-[80px] font-black text-white leading-none transition-transform group-hover:scale-105">
                  {formatImpactCount(impact?.clicks ?? data.lifetimeClicks)}
                </div>
                <div className="text-xl font-bold text-white uppercase tracking-wider">
                  {t("impact.audienceFoundJobs")}
                </div>
              </div>
              <div className="space-y-4 group">
                <div className="text-[64px] sm:text-[80px] font-black text-white leading-none transition-transform group-hover:scale-105">
                  {formatImpactCount(impact?.applications ?? data.lifetimeApplications)}
                </div>
                <div className="text-xl font-bold text-white uppercase tracking-wider">
                  {t("impact.jobApplications")}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 3: TESTIMONIALS */}
        <section className="relative overflow-hidden bg-referral-surface py-32">
          <div className="mx-auto container max-w-7xl px-6">
            <div className="text-center ">
              <h2 className="font-bree text-[42px] sm:text-[52px] leading-tight inline-block">
                {t("testimonials.headingPrefix")}
                <span className="relative inline-block mx-4">
                  <span className="relative z-10 inline-block -rotate-4 border-2 border-black bg-referral-marquee px-6 py-2 text-white shadow-referral-sm">{t("testimonials.headingHighlight")}</span>
                </span>
              </h2>
              <p className="mt-4 text-xl font-plus-jakarta-sans text-referral-ink">
                {t("testimonials.subtitle")}
              </p>
            </div>

            <div className="relative">
              <div className="absolute -top-3  z-1 pointer-events-none">
                <img src={REFERRAL_INVITE_URLS.decorativeStar} alt="" className="block h-48 w-48" />
              </div>
              <Carousel className="w-full relative pt-16 z-2" opts={{ align: "start", loop: true }}>
                <CarouselContent className="ml-0 overflow-visible">
                  {REFERRAL_INVITE_TESTIMONIALS.map((testimonial, index) => (
                    <CarouselItem key={index} className="pl-0 relative pb-12 overflow-visible">
                      <div className="relative flex flex-col lg:flex-row items-center gap-6 lg:gap-0">

                        {/* Profile/Success Card (Mobile: Top, Desktop: Absolute Right) */}
                        <div className="block w-[94%] mx-auto lg:mx-0 lg:absolute lg:right-0 lg:top-1/2 lg:-translate-y-1/2 lg:w-[55%] z-20">
                          <div className="relative w-full overflow-hidden rounded-[24px] bg-referral-video aspect-16/10 lg:aspect-16/10 lg:rounded-[32px]">
                            {playingVideo === testimonial.videoUrl ? (
                              <iframe
                                src={testimonial.videoUrl}
                                className="w-full h-full"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                allowFullScreen
                              />
                            ) : (
                              <>
                                <img
                                  src={testimonial.thumbnailUrl}
                                  alt={t(`testimonials.items.${testimonial.id}.thumbnailAlt`, {
                                    author: t(`testimonials.items.${testimonial.id}.author`),
                                  })}
                                  className="w-full h-full object-cover"
                                />
                                {testimonial.videoUrl && (
                                  <button
                                    onClick={() => setPlayingVideo(testimonial.videoUrl!)}
                                    className="absolute inset-0 flex items-center justify-center bg-black/10 hover:bg-black/20 transition-all group/play"
                                  >
                                    <img
                                      src={REFERRAL_INVITE_URLS.playButton}
                                      alt={t("testimonials.playVideo")}
                                      className="w-20 h-20 transition-transform group-hover/play:scale-110"
                                    />
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        </div>

                        {/* Testimonial Text Card */}
                        <div className="relative z-10 mx-auto flex min-h-0 w-[94%] flex-col justify-center rounded-[16px] border-2 border-black bg-white p-6 shadow-referral-md lg:mx-0 lg:min-h-[550px] lg:w-[65%] lg:pt-[35px] lg:pr-[20%] lg:pb-[35px] lg:pl-[35px] lg:shadow-referral-lg">
                          <div className="space-y-6 lg:space-y-8 relative">
                            <div className="mb-4 lg:mb-6">
                              <img src={REFERRAL_INVITE_URLS.quoteImage} alt={t("testimonials.quoteAlt")} width={89} height={77} className="h-auto w-12 lg:w-20" />
                            </div>

                            <div className="space-y-4 text-sm font-plus-jakarta-sans leading-relaxed text-referral-muted lg:space-y-6 lg:text-[15px]">
                              {testimonial.paragraphKeys.map((paragraphKey) => (
                                <p key={paragraphKey} className={paragraphKey === "paragraph2" && testimonial.id === "akshataNBhatt" ? "font-semibold text-black" : undefined}>
                                  {t.rich(`testimonials.items.${testimonial.id}.${paragraphKey}`, {
                                    link: (chunks) => (
                                      <a
                                        href={REFERRAL_INVITE_URLS.arthaHome}
                                        className="font-bold text-referral-accent hover:underline"
                                      >
                                        {chunks}
                                      </a>
                                    ),
                                    br: () => <br />,
                                  })}
                                </p>
                              ))}
                            </div>

                            <div className="pt-6 lg:pt-8 font-plus-jakarta-sans">
                              <div className="inline-block border-b-2 hover:border-primary border-black">
                                <a href={testimonial.profileLink} target="_blank" rel="noopener noreferrer" className="text-xl font-semibold text-referral-ink hover:text-primary lg:text-2xl">
                                  {t(`testimonials.items.${testimonial.id}.author`)}
                                </a>
                              </div>
                              <p className="mt-1 text-sm text-referral-subtle lg:mt-2">
                                {t(`testimonials.items.${testimonial.id}.bio`)}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>

                {/* Standardized Navigation Arrows - Both using pink neubrutalist style */}
                <CarouselPrevious className="absolute left-0 top-1/2 z-50 h-12 w-12 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-black bg-referral-accent text-white opacity-100 shadow-referral-chip transition-all hover:scale-105 hover:bg-referral-accent active:translate-y-[1px] disabled:opacity-100">
                  <ChevronLeft className="h-6 w-6 stroke-3" />
                </CarouselPrevious>

                <CarouselNext className="absolute right-0 top-1/2 z-50 h-12 w-12 -translate-y-1/2 rounded-full border-2 border-black bg-referral-accent text-white opacity-100 shadow-referral-chip transition-all hover:scale-105 hover:bg-referral-accent active:translate-y-[1px] disabled:opacity-100">
                  <ChevronRight className="h-6 w-6 stroke-3" />
                </CarouselNext>
              </Carousel>
            </div>
          </div>
        </section>

        {/* SECTION 4: FINAL CTA */}
        <section className="relative overflow-hidden bg-referral-blue py-24 sm:py-32">
          {/* Decorative Background Shapes */}
          <div
            className="absolute top-0 sm:left-12 sm:top-12 opacity-100 z-0 rotate-15"
          >
            <BrutalismShape className="w-[100px] h-[100px] lg:w-[150px] lg:h-[150px]" />
          </div>

          <div
            className="absolute right-0 bottom-0 opacity-100 z-0"
          >
            <BauhausShape className="w-[100px] h-[100px] lg:w-[250px] lg:h-[250px]" />
          </div>

          <div className="mx-auto container max-w-4xl px-6 text-center relative z-10">
            <div className="flex flex-col items-center gap-8 sm:gap-10">
              <h2 className="font-bree text-[28px] sm:text-[46px] leading-[35px] sm:leading-[60px] text-white">
                {t("finalCta.heading")}
              </h2>

              <div className="relative inline-block rotate-[-2.5deg] mb-4 sm:mb-8">
                <div className="absolute inset-0 bg-black translate-x-1.5 translate-y-1.5 rounded-sm" />
                <div className="relative z-10 border-2 border-black bg-referral-marquee px-8 py-4 shadow-referral-sm">
                  <span className="font-bree text-[20px] sm:text-[44px] leading-[35px] sm:leading-[44px] text-white uppercase tracking-tight sm:whitespace-nowrap">
                    {t("finalCta.banner")}
                  </span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-6 w-full">
                <button
                  type="button"
                  onClick={() => {
                    void handleSignupClick();
                  }}
                  disabled={isStartingSignup}
                  className="group relative w-full sm:w-auto"
                >
                  <div className="absolute inset-0 bg-black translate-x-2 translate-y-2 rounded-xl" />
                  <div className="relative z-10 flex h-16 items-center justify-center rounded-xl border-2 border-black bg-white px-12 text-base font-semibold font-plus-jakarta-sans text-referral-blue transition-transform group-hover:-translate-x-1 group-hover:-translate-y-1 active:translate-x-1 active:translate-y-1 sm:text-[20px]">
                    {t("finalCta.cta")}
                    <ArrowRight className="ml-3 h-5 w-5 stroke-3" />
                  </div>
                </button>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
